import type { Point2D } from '../helpers/math.ts';

import { Spring1D, SpringAnimator, SPRING_PRESETS } from './SpringAnimator.ts';

export type CameraState = {
    x: number;
    y: number;
    scale: number;
};

type CameraOptions = {
    viewportWidth: number;
    viewportHeight: number;
    contentWidth: number;
    contentHeight: number;
    roomWidthMm: number;
    minScale?: number;
    maxScale?: number;
    onUpdate: () => void;
};

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function rubberBand(offset: number, dimension: number, constant = 0.55): number {
    const absOffset = Math.abs(offset);
    if (absOffset < 0.01) return 0;
    return (Math.sign(offset) * (absOffset * dimension * constant)) / (dimension + constant * absOffset);
}

function computeAdaptiveInitialScale(roomWidthMm: number): number {
    if (roomWidthMm < 6000) return 0.6;
    if (roomWidthMm < 10000) {
        const t = (roomWidthMm - 6000) / 4000;
        return 0.6 + t * 0.25;
    }
    return 1.0;
}

export class Camera {
    private animator: SpringAnimator;
    private xSpring: Spring1D;
    private ySpring: Spring1D;
    private scaleSpring: Spring1D;

    private gesturing = false;
    private gestureX = 0;
    private gestureY = 0;
    private gestureScale = 1;

    private vpWidth: number;
    private vpHeight: number;
    private contentW: number;
    private contentH: number;

    minScale: number;
    readonly maxScale: number;
    initialScale: number;
    private roomWidthMm: number;

    constructor(options: CameraOptions) {
        this.vpWidth = options.viewportWidth;
        this.vpHeight = options.viewportHeight;
        this.contentW = options.contentWidth;
        this.contentH = options.contentHeight;
        this.roomWidthMm = options.roomWidthMm;
        this.maxScale = options.maxScale ?? 3;

        // Initial scale: ensure all content is visible (fit both horizontally and vertically)
        const adaptiveScale = computeAdaptiveInitialScale(options.roomWidthMm);
        const fitHorizontal = options.viewportWidth / options.contentWidth;
        const fitVertical = options.viewportHeight / options.contentHeight;
        this.initialScale = Math.min(adaptiveScale, fitHorizontal, fitVertical);
        this.minScale = Math.min(options.minScale ?? 0.5, this.initialScale);

        this.animator = new SpringAnimator(() => options.onUpdate());

        const homeBounds = this.getPanBounds(this.initialScale);
        const homeX = (homeBounds.minX + homeBounds.maxX) / 2;
        const homeY = (homeBounds.minY + homeBounds.maxY) / 2;

        this.xSpring = new Spring1D(homeX, SPRING_PRESETS.pan);
        this.ySpring = new Spring1D(homeY, SPRING_PRESETS.pan);
        this.scaleSpring = new Spring1D(this.initialScale, SPRING_PRESETS.zoom);

        this.animator.addSpring('x', this.xSpring);
        this.animator.addSpring('y', this.ySpring);
        this.animator.addSpring('scale', this.scaleSpring);
    }

    private getHomePosition(): { x: number; y: number } {
        const bounds = this.getPanBounds(this.initialScale);
        return {
            x: (bounds.minX + bounds.maxX) / 2,
            y: (bounds.minY + bounds.maxY) / 2
        };
    }

    get state(): CameraState {
        if (this.gesturing) {
            return { x: this.gestureX, y: this.gestureY, scale: this.gestureScale };
        }
        return {
            x: this.xSpring.position,
            y: this.ySpring.position,
            scale: this.scaleSpring.position
        };
    }

    updateViewport(vpWidth: number, vpHeight: number, contentWidth: number, contentHeight: number): void {
        const wasHome = !this.gesturing && Math.abs(this.scaleSpring.position - this.initialScale) < 0.001;
        this.vpWidth = vpWidth;
        this.vpHeight = vpHeight;
        this.contentW = contentWidth;
        this.contentH = contentHeight;

        // Recompute initial scale to fit all content
        const adaptiveScale = computeAdaptiveInitialScale(this.roomWidthMm);
        const fitH = vpWidth / contentWidth;
        const fitV = vpHeight / contentHeight;
        this.initialScale = Math.min(adaptiveScale, fitH, fitV);
        this.minScale = Math.min(0.5, this.initialScale);

        // A resized map (the seat category chip takes room below it) refits what the guest has not moved or zoomed,
        // and keeps a moved view inside the new bounds, rather than leaving rows cut off by the shorter canvas.
        if (this.gesturing) return;
        this.animator.stop();
        if (wasHome) {
            const home = this.getHomePosition();
            this.xSpring.snapTo(home.x);
            this.ySpring.snapTo(home.y);
            this.scaleSpring.snapTo(this.initialScale);
        } else {
            const bounds = this.getPanBounds(this.scaleSpring.position);
            this.xSpring.snapTo(clamp(this.xSpring.position, bounds.minX, bounds.maxX));
            this.ySpring.snapTo(clamp(this.ySpring.position, bounds.minY, bounds.maxY));
        }
    }

    // --- Gesture lifecycle ---

    beginGesture(): void {
        // If previous gesture wasn't properly ended, commit gesture state to springs
        if (this.gesturing) {
            this.xSpring.position = this.gestureX;
            this.ySpring.position = this.gestureY;
            this.scaleSpring.position = this.gestureScale;
        }
        this.gesturing = true;
        this.animator.stop();
        this.gestureX = this.xSpring.position;
        this.gestureY = this.ySpring.position;
        this.gestureScale = this.scaleSpring.position;
    }

    updatePan(dx: number, dy: number): void {
        if (!this.gesturing) return;

        const rawX = this.gestureX + dx;
        const rawY = this.gestureY + dy;
        const bounds = this.getPanBounds(this.gestureScale);

        this.gestureX = this.applyRubberBand(rawX, bounds.minX, bounds.maxX, this.vpWidth);
        this.gestureY = this.applyRubberBand(rawY, bounds.minY, bounds.maxY, this.vpHeight);
    }

    /**
     * Two-finger gesture, as in the studio plan: the point that was under the starting midpoint stays
     * under the fingers' current midpoint (pan) while their spread scales the map (pinch), both at once.
     * `ratio` is the finger distance now over the distance at `start`.
     */
    pinchTo(start: CameraState, startFocal: Point2D, ratio: number, focal: Point2D): void {
        if (!this.gesturing) return;

        const scale = clamp(start.scale * ratio, this.minScale * 0.8, this.maxScale * 1.2);
        const k = scale / start.scale;
        const bounds = this.getPanBounds(scale);

        this.gestureScale = scale;
        this.gestureX = this.applyRubberBand(
            focal.x - k * (startFocal.x - start.x),
            bounds.minX,
            bounds.maxX,
            this.vpWidth
        );
        this.gestureY = this.applyRubberBand(
            focal.y - k * (startFocal.y - start.y),
            bounds.minY,
            bounds.maxY,
            this.vpHeight
        );
    }

    endPan(velocityX = 0, velocityY = 0): void {
        if (!this.gesturing) return;
        this.gesturing = false;

        const bounds = this.getPanBounds(this.gestureScale);
        const isOutOfBounds =
            this.gestureX < bounds.minX ||
            this.gestureX > bounds.maxX ||
            this.gestureY < bounds.minY ||
            this.gestureY > bounds.maxY;

        const preset = isOutOfBounds ? SPRING_PRESETS.bounceBack : SPRING_PRESETS.pan;

        this.xSpring.position = this.gestureX;
        this.xSpring.stiffness = preset.stiffness;
        this.xSpring.damping = preset.damping;

        this.ySpring.position = this.gestureY;
        this.ySpring.stiffness = preset.stiffness;
        this.ySpring.damping = preset.damping;

        // Predict landing position from momentum
        const momentumFactor = 0.3;
        const predictedX = this.gestureX + velocityX * momentumFactor;
        const predictedY = this.gestureY + velocityY * momentumFactor;

        this.xSpring.setTarget(clamp(predictedX, bounds.minX, bounds.maxX), velocityX);
        this.ySpring.setTarget(clamp(predictedY, bounds.minY, bounds.maxY), velocityY);

        this.scaleSpring.position = this.gestureScale;
        this.animator.start();
    }

    endScale(): void {
        if (!this.gesturing) return;
        this.gesturing = false;

        this.xSpring.position = this.gestureX;
        this.ySpring.position = this.gestureY;
        this.scaleSpring.position = this.gestureScale;

        // Snap scale to valid range if overshot
        const clampedScale = clamp(this.gestureScale, this.minScale, this.maxScale);
        if (clampedScale !== this.gestureScale) {
            const ratio = clampedScale / this.gestureScale;
            const cx = this.vpWidth / 2;
            const cy = this.vpHeight / 2;
            this.xSpring.setTarget(cx - ratio * (cx - this.gestureX));
            this.ySpring.setTarget(cy - ratio * (cy - this.gestureY));
            this.scaleSpring.stiffness = SPRING_PRESETS.bounceBack.stiffness;
            this.scaleSpring.damping = SPRING_PRESETS.bounceBack.damping;
            this.scaleSpring.setTarget(clampedScale);
        } else {
            const bounds = this.getPanBounds(clampedScale);
            this.xSpring.setTarget(clamp(this.gestureX, bounds.minX, bounds.maxX));
            this.ySpring.setTarget(clamp(this.gestureY, bounds.minY, bounds.maxY));
            this.scaleSpring.setTarget(clampedScale);
        }

        this.animator.start();
    }

    // --- Programmatic animations ---

    doubleTap(focal: Point2D): void {
        this.gesturing = false;
        const currentScale = this.scaleSpring.position;
        const isZoomedIn = currentScale > this.initialScale + 0.1;

        const preset = SPRING_PRESETS.doubleTap;
        this.xSpring.stiffness = preset.stiffness;
        this.xSpring.damping = preset.damping;
        this.ySpring.stiffness = preset.stiffness;
        this.ySpring.damping = preset.damping;
        this.scaleSpring.stiffness = preset.stiffness;
        this.scaleSpring.damping = preset.damping;

        if (isZoomedIn) {
            // Reset to initial view (centered)
            const home = this.getHomePosition();
            this.xSpring.setTarget(home.x);
            this.ySpring.setTarget(home.y);
            this.scaleSpring.setTarget(this.initialScale);
        } else {
            // Zoom to 2x at focal point
            const targetScale = Math.min(2, this.maxScale);
            const ratio = targetScale / currentScale;
            const newX = focal.x - ratio * (focal.x - this.xSpring.position);
            const newY = focal.y - ratio * (focal.y - this.ySpring.position);

            const bounds = this.getPanBounds(targetScale);
            this.xSpring.setTarget(clamp(newX, bounds.minX, bounds.maxX));
            this.ySpring.setTarget(clamp(newY, bounds.minY, bounds.maxY));
            this.scaleSpring.setTarget(targetScale);
        }

        this.animator.start();
    }

    /**
     * Trackpad scroll and wheel zoom, applied at once rather than through a spring so the map stays under
     * the fingers. Zooms by `factor` around `focal`, then pans by (dx, dy), within the pan bounds. Returns
     * whether the view moved: a scroll that hits the bounds is left to the page.
     */
    jumpBy(dx: number, dy: number, factor = 1, focal: Point2D = { x: 0, y: 0 }): boolean {
        if (this.gesturing) return false;
        this.animator.stop();

        const { x, y, scale } = this.state;
        const nextScale = clamp(scale * factor, this.minScale, this.maxScale);
        const k = nextScale / scale;
        const bounds = this.getPanBounds(nextScale);
        const nextX = clamp(focal.x - k * (focal.x - x) + dx, bounds.minX, bounds.maxX);
        const nextY = clamp(focal.y - k * (focal.y - y) + dy, bounds.minY, bounds.maxY);
        if (nextX === x && nextY === y && nextScale === scale) return false;

        this.xSpring.snapTo(nextX);
        this.ySpring.snapTo(nextY);
        this.scaleSpring.snapTo(nextScale);
        return true;
    }

    resetToInitial(): void {
        const preset = SPRING_PRESETS.doubleTap;
        this.xSpring.stiffness = preset.stiffness;
        this.xSpring.damping = preset.damping;
        this.ySpring.stiffness = preset.stiffness;
        this.ySpring.damping = preset.damping;
        this.scaleSpring.stiffness = preset.stiffness;
        this.scaleSpring.damping = preset.damping;

        const home = this.getHomePosition();
        this.xSpring.setTarget(home.x);
        this.ySpring.setTarget(home.y);
        this.scaleSpring.setTarget(this.initialScale);
        this.animator.start();
    }

    // --- Coordinate transforms ---

    screenToWorld(point: Point2D): Point2D {
        const { x, y, scale } = this.state;
        return {
            x: (point.x - x) / scale,
            y: (point.y - y) / scale
        };
    }

    worldToScreen(point: Point2D): Point2D {
        const { x, y, scale } = this.state;
        return {
            x: point.x * scale + x,
            y: point.y * scale + y
        };
    }

    // --- Pan bounds ---

    private getPanBounds(scale: number) {
        const scaledW = this.contentW * scale;
        const scaledH = this.contentH * scale;

        if (scaledW <= this.vpWidth) {
            // Content fits horizontally: center it
            const centerX = (this.vpWidth - scaledW) / 2;
            return {
                minX: centerX,
                maxX: centerX,
                minY: scaledH <= this.vpHeight ? (this.vpHeight - scaledH) / 2 : this.vpHeight - scaledH,
                maxY: scaledH <= this.vpHeight ? (this.vpHeight - scaledH) / 2 : 0
            };
        }

        return {
            minX: this.vpWidth - scaledW,
            maxX: 0,
            minY: scaledH <= this.vpHeight ? (this.vpHeight - scaledH) / 2 : this.vpHeight - scaledH,
            maxY: scaledH <= this.vpHeight ? (this.vpHeight - scaledH) / 2 : 0
        };
    }

    private applyRubberBand(value: number, min: number, max: number, dimension: number): number {
        if (value < min) {
            return min + rubberBand(value - min, dimension);
        }
        if (value > max) {
            return max + rubberBand(value - max, dimension);
        }
        return value;
    }

    isAnimating(): boolean {
        return this.animator.isAnimating();
    }

    destroy(): void {
        this.animator.destroy();
    }
}
